/**
 * MediaUrlInput - Componente reutilizável para seleção de URL de mídia
 * 
 * Combina:
 * - Campo de texto para URL manual
 * - Botão para abrir galeria de mídia do tenant
 * - Preview opcional da imagem selecionada
 * 
 * Permite ao usuário digitar uma URL manualmente ou selecionar
 * diretamente de sua galeria de imagens existente.
 * 
 * @version 1.0.0
 */

import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Image, X } from 'lucide-react';
import { MediaGalleryDialog } from '@/components/tenant/media/MediaGalleryDialog';
import { MediaGalleryItem, MediaFileType } from '@/types/media';
import { getStorageUrl } from '@/lib/storage';
import { createLogger } from '@/lib/logger';
import { cn } from '@/lib/utils';
import { FileText as FileIcon } from 'lucide-react';

const logger = createLogger('MediaUrlInput');

/**
 * Extrai apenas o nome do arquivo + extensão de uma URL completa
 * Remove todo o caminho do bucket, retornando somente o último segmento
 * 
 * @example
 * extractFileName('https://xyz.supabase.co/storage/.../imagem.png') => 'imagem.png'
 */
function extractFileName(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    const segments = pathname.split('/');
    return decodeURIComponent(segments[segments.length - 1]) || url;
  } catch {
    const segments = url.split('/');
    return decodeURIComponent(segments[segments.length - 1]) || url;
  }
}

/**
 * Verifica se a URL aponta para uma imagem baseado na extensão
 */
function isImageUrl(url: string): boolean {
  const fileName = extractFileName(url).toLowerCase();
  return /\.(jpg|jpeg|png|gif|webp|svg|bmp|ico)$/.test(fileName);
}

/**
 * Props do componente MediaUrlInput
 */
interface MediaUrlInputProps {
  /** Valor atual da URL */
  value: string | null;
  /** Callback quando URL é alterada */
  onChange: (url: string | null) => void;
  /** Label exibido acima do input (opcional) */
  label?: string;
  /** Placeholder do input */
  placeholder?: string;
  /** Se deve mostrar preview da imagem quando URL preenchida */
  showPreview?: boolean;
  /** Tipos de mídia permitidos para seleção na galeria */
  allowedTypes?: MediaFileType[];
  /** Classe CSS adicional para o container */
  className?: string;
}

export function MediaUrlInput({
  value,
  onChange,
  label,
  placeholder = 'https://exemplo.com/imagem.png',
  showPreview = false,
  allowedTypes = ['image'],
  className,
}: MediaUrlInputProps) {
  // Estado para controlar abertura do dialog da galeria
  const [galleryOpen, setGalleryOpen] = useState(false);
  // Estado para controlar erro de carregamento do preview
  const [previewError, setPreviewError] = useState(false);

  /**
   * Handler quando usuário seleciona uma mídia da galeria
   * Converte o storage_path em URL pública e fecha o dialog
   */
  const handleMediaSelect = (media: MediaGalleryItem) => {
    logger.debug('Mídia selecionada da galeria', { 
      mediaId: media.id, 
      storagePath: media.storage_path 
    });
    
    // Converte o caminho do storage para URL pública
    const url = getStorageUrl(media.storage_path);
    onChange(url || null);
    setGalleryOpen(false);
    setPreviewError(false);
  };

  /**
   * Handler para mudança manual no input
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value || null;
    onChange(newValue);
    setPreviewError(false);
  };

  /**
   * Handler para limpar a URL
   */
  const handleClear = () => {
    onChange(null);
    setPreviewError(false);
  };

  /**
   * Handler para erro de carregamento do preview
   */
  const handlePreviewError = () => {
    setPreviewError(true);
  };

  return (
    <div className={cn("w-full", className)}>
      {/* Label opcional */}
      {label && <Label className="mb-2 block">{label}</Label>}
      
      {/* Modo sem valor: Input + botão galeria */}
      {!value && (
        <div className="flex gap-2 min-w-0">
          <Input
            value=""
            onChange={handleInputChange}
            placeholder={placeholder}
            className="flex-1 min-w-0"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={() => setGalleryOpen(true)}
            title="Selecionar da galeria de mídia"
          >
            <Image className="h-4 w-4" />
          </Button>
        </div>
      )}

      {/* Modo com valor: Preview compacto com nome do arquivo */}
      {value && (
        <div className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30 min-w-0 overflow-hidden">
          {/* Thumbnail para imagens ou ícone para documentos */}
          {showPreview && isImageUrl(value) && !previewError ? (
            <img
              src={value}
              alt="Preview"
              className="w-10 h-10 object-cover rounded border flex-shrink-0"
              onError={handlePreviewError}
            />
          ) : (
            <FileIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
          )}
          {/* Nome do arquivo extraído da URL */}
          <span className="text-xs text-muted-foreground truncate flex-1 min-w-0 max-w-[200px]">
            {extractFileName(value)}
          </span>
          {/* Botão trocar via galeria */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setGalleryOpen(true)}
            title="Trocar arquivo"
          >
            <Image className="h-4 w-4" />
          </Button>
          {/* Botão remover */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClear}
            title="Remover arquivo"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {/* Dialog da Galeria de Mídia */}
      <MediaGalleryDialog
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        selectionMode={true}
        onSelect={handleMediaSelect}
      />
    </div>
  );
}
